/**
 * Typed REST client — targets the Hono backend in apps/api. The OpenAPI spec
 * (once route registration lands) will live at `${API_URL}/openapi.json`;
 * regenerate `schema.d.ts` from it via `bun run gen:api`.
 *
 * Use the named hooks in `./hooks` from React components instead of touching
 * `api` directly. This module exports `api` only so non-React call sites
 * (e.g. zustand stores) can still get type-safe calls.
 */

import createClient from "openapi-fetch";
import type { paths } from "./schema";
import { API_URL } from "@/config/env";

// Token getter and signOut are injected once at app boot from the Clerk
// provider via the bridge in `lib/clerk-bridge.tsx`. We avoid pulling Clerk
// into this module so it stays usable from non-React contexts (and so tests
// can stub it cheaply).
let getToken: () => Promise<string | null> = async () => null;
let signOut: () => Promise<void> = async () => {};
let isSigningOut = false;

const userMessageByStatus: Record<number, string> = {
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  413: "That upload is too large.",
  429: "You're going too fast. Please slow down.",
  500: "Something went wrong on our end. Please try again.",
  502: "We're having trouble reaching our servers. Please try again.",
  503: "Service is temporarily unavailable. Please try again.",
  504: "The request timed out. Please try again.",
};

export function setAuthTokenGetter(fn: () => Promise<string | null>) {
  getToken = fn;
}

export function setAuthSignOut(fn: () => Promise<void>) {
  signOut = fn;
}

// Triggered on the first 401 response. Clerk's session is invalid (expired,
// revoked, or never issued) — keeping the user on a "logged-in" page just
// produces more 401s. Sign out, then hard-redirect to /login so all in-memory
// React Query / Zustand state gets wiped. Idempotent: parallel 401s only fire
// the redirect once.
function handleAuthFailure() {
  if (isSigningOut || typeof window === "undefined") return;
  // Don't redirect if we're already on an auth page — Clerk's <SignIn> /
  // <SignUp> components don't make authed API calls, but defensive against
  // any future addition that would.
  if (/^\/(login|register)/.test(window.location.pathname)) return;
  isSigningOut = true;
  signOut()
    .catch((err) => {
      console.error("[auth] signOut during 401 recovery failed:", err);
    })
    .finally(() => {
      window.location.assign("/login");
    });
}

// Backend mounts all resource routes under `/v1`. Paths in the typed schema
// are written without the prefix (e.g. `/dashboard/subjects`) so the baseUrl
// carries it. For non-versioned endpoints (`/healthz`), call API_URL directly.
export const api = createClient<paths>({
  baseUrl: `${API_URL}/v1`,
});

// Global middleware: attach the Clerk JWT and surface RFC 7807 problem+json
// errors as throwable Error objects.
api.use({
  async onRequest({ request }) {
    const token = await getToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 401) {
      handleAuthFailure();
    }
    if (!response.ok) {
      const ct = response.headers.get("Content-Type") ?? "";
      if (ct.includes("application/problem+json")) {
        const body = (await response.clone().json()) as {
          title?: string;
          detail?: string;
          code?: string;
        };
        const friendly = userMessageByStatus[response.status] ?? "Something went wrong.";
        // 400 validation errors are usually the most actionable text the user
        // can see; everything else uses the friendly default. The dev-facing
        // detail is preserved on the error object for DevTools / logging.
        const userMessage =
          response.status === 400 ? (body.detail ?? friendly) : friendly;
        const err = new Error(userMessage) as Error & {
          code?: string;
          status?: number;
          devMessage?: string;
        };
        err.code = body.code;
        err.status = response.status;
        err.devMessage = body.detail;
        throw err;
      }
    }
    return response;
  },
});
